import React from 'react';

import { useDateRefresh } from '@gitbook/util-react-hooks';

import { toLocaleDateTimeString } from '../Intl';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 31 * DAY;
const YEAR = 365 * DAY;

/**
 * Hook to use a relative date. Handles SSR refreshing and any locale management.
 */
export function useDateShortRelative(
    input: Date | string,
    options: { onlyDate?: boolean } = { onlyDate: true }
): {
    /** The date value */
    date: Date;
    /** A human-readable relative value for the date */
    value: string;
    /** An accessible value for the date */
    accessibilityValue: string;
    /** A tooltip for the date */
    tooltip: string;
} {
    const { onlyDate = true } = options;

    const value = React.useMemo(() => {
        return new Date(input);
    }, [input]);

    const timestampNow = process.env.GITBOOK_PREDICTABLE_UI
        ? new Date(2021, 1, 1).getTime()
        : Date.now();

    const period = Math.max(1000, Math.abs(timestampNow - value.getTime()));
    const [years, months, days, hours, minutes] = [
        period / YEAR,
        period / MONTH,
        period / DAY,
        period / HOUR,
        period / MINUTE,
    ].map((n) => Math.floor(n));
    const isFuture = value.getTime() > timestampNow;
    const [readableValue, setReadableValue] = React.useState(
        `${value.toISOString().substr(0, 16).replace('T', ' ')} UTC`
    );

    // we are only going to use a local date format after render
    // to avoid mismatch between SSR and hydration
    React.useEffect(() => {
        setReadableValue(
            value.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
            })
        );
    }, [value, setReadableValue]);

    useDateRefresh(value);

    const prefix = !onlyDate && isFuture ? 'in ' : '';
    const suffix = !onlyDate && !isFuture ? ' ago' : '';

    let humanPeriod: string;
    if (years >= 1) {
        humanPeriod = `${prefix}${years}yr${suffix}`;
    } else if (months >= 1) {
        humanPeriod = `${prefix}${months}mo${suffix}`;
    } else if (days >= 1) {
        humanPeriod = `${prefix}${days}d${suffix}`;
    } else if (hours >= 1) {
        humanPeriod = `${prefix}${hours}h${suffix}`;
    } else if (minutes >= 1) {
        humanPeriod = `${prefix}${minutes}m${suffix}`;
    } else {
        humanPeriod = 'now';
    }

    const tooltip = toLocaleDateTimeString(value);

    return { date: value, value: humanPeriod, accessibilityValue: readableValue, tooltip };
}
